import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { promptsAPI, ratingsAPI, autoPromptsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import CommentSection from '../components/CommentSection';
import SEOHead from '../components/SEOHead';
import ShareButtons from '../components/ShareButtons';
import PromptHeader from '../components/prompt/PromptHeader';
import RatingsSection from '../components/prompt/RatingsSection';
import PromptStats from '../components/prompt/PromptStats';
import { LoadingSpinner, Card } from '../components/ui';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.boopboop.io';

/**
 * PromptDetail - Refactored and simplified
 * Split into sub-components for better organization and reduced clutter
 */
const PromptDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [prompt, setPrompt] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [ratingStats, setRatingStats] = useState(null);
  const [myRating, setMyRating] = useState(null);
  const [loading, setLoading] = useState(true);

  const [ratingFormData, setRatingFormData] = useState({
    overall_rating: 5,
    clarity_rating: 5,
    creativity_rating: 5,
    effectiveness_rating: 5,
    comment: '',
  });

  useEffect(() => {
    fetchPromptData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPromptData = async () => {
    setLoading(true);
    try {
      // Fetch prompt details
      const promptResponse = await promptsAPI.getById(id);
      setPrompt(promptResponse.data.prompt);

      // Fetch ratings
      const ratingsResponse = await ratingsAPI.getByPromptId(id, { limit: 10 });
      setRatings(ratingsResponse.data.ratings || []);

      // Fetch rating stats
      const statsResponse = await ratingsAPI.getStats(id);
      setRatingStats(statsResponse.data.stats);

      // Fetch my rating if logged in
      if (currentUser) {
        try {
          const myRatingResponse = await ratingsAPI.getMyRating(id);
          if (myRatingResponse.data.rating) {
            setMyRating(myRatingResponse.data.rating);
            setRatingFormData({
              overall_rating: myRatingResponse.data.rating.overall_rating,
              clarity_rating: myRatingResponse.data.rating.clarity_rating,
              creativity_rating: myRatingResponse.data.rating.creativity_rating,
              effectiveness_rating: myRatingResponse.data.rating.effectiveness_rating,
              comment: myRatingResponse.data.rating.comment || '',
            });
          }
        } catch (error) {
          // No rating yet - that's okay
          console.log('No existing rating');
        }
      }
    } catch (error) {
      console.error('Fetch prompt error:', error);
      toast.error('Failed to load prompt');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this prompt? This action cannot be undone.')) {
      return;
    }

    try {
      await promptsAPI.delete(id);
      toast.success('Prompt deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      console.error('Delete prompt error:', error);
      toast.error('Failed to delete prompt');
    }
  };

  const handleFork = async () => {
    try {
      const response = await promptsAPI.fork(id, {
        note: 'Forked from original',
      });
      toast.success('Prompt forked successfully!');
      navigate(`/prompts/${response.data.prompt.id}`);
    } catch (error) {
      console.error('Fork prompt error:', error);
      toast.error('Failed to fork prompt');
    }
  };

  const canClaim = () => {
    if (!prompt || !currentUser) return false;
    if (userProfile && prompt.user_id === userProfile.id) return false;
    return Array.isArray(prompt.tags) && prompt.tags.includes('bot');
  };

  const handleClaim = async () => {
    if (!currentUser) {
      toast.error('Please log in to claim prompts');
      navigate('/login');
      return;
    }
    try {
      const res = await autoPromptsAPI.claimByPrompt(id);
      toast.success(res.data?.message || 'Claim submitted');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to submit claim';
      toast.error(msg);
    }
  };

  const handleRatingSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      toast.error('Please log in to rate prompts');
      navigate('/login');
      return;
    }

    try {
      if (myRating) {
        // Update existing rating - convert to camelCase
        await ratingsAPI.update(myRating.id, {
          overallRating: ratingFormData.overall_rating,
          clarityRating: ratingFormData.clarity_rating,
          creativityRating: ratingFormData.creativity_rating,
          effectivenessRating: ratingFormData.effectiveness_rating,
          comment: ratingFormData.comment,
        });
        toast.success('Rating updated successfully!');
      } else {
        // Create new rating - convert to camelCase
        await ratingsAPI.create({
          promptId: id,
          overallRating: ratingFormData.overall_rating,
          clarityRating: ratingFormData.clarity_rating,
          creativityRating: ratingFormData.creativity_rating,
          effectivenessRating: ratingFormData.effectiveness_rating,
          comment: ratingFormData.comment,
        });
        toast.success('Rating submitted successfully!');
      }
      fetchPromptData(); // Refresh data
    } catch (error) {
      console.error('Rating error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit rating';
      toast.error(errorMessage);
    }
  };

  const handleDeleteRating = async () => {
    if (!window.confirm('Are you sure you want to delete your rating?')) {
      return;
    }

    try {
      await ratingsAPI.delete(myRating.id);
      toast.success('Rating deleted');
      setMyRating(null);
      fetchPromptData();
    } catch (error) {
      console.error('Delete rating error:', error);
      toast.error('Failed to delete rating');
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <LoadingSpinner size="lg" text="Loading prompt..." />
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <p className="text-center py-12 text-gray-600 dark:text-gray-400">Prompt not found</p>
        </Card>
      </div>
    );
  }

  const isOwner = currentUser && userProfile && prompt.user_id === userProfile.id;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <SEOHead
        title={prompt.title}
        description={
          prompt.description ||
          prompt.prompt_text?.substring(0, 160) ||
          `Check out this ${prompt.category} prompt with a ${
            typeof ratingStats?.overall_average === 'number'
              ? ratingStats.overall_average.toFixed(1)
              : 'N/A'
          }/10 rating!`
        }
        image={`${API_BASE_URL}/og/${id}.png`}
        url={`${window.location.origin}/prompts/${id}`}
        type="article"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <PromptHeader
            prompt={prompt}
            currentUser={currentUser}
            isOwner={isOwner}
            onDelete={handleDelete}
            onFork={handleFork}
            onClaim={handleClaim}
            canClaim={canClaim}
          />

          {/* Share Buttons */}
          <Card padding="comfortable">
            <div className="flex flex-col space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Share this prompt
              </h3>
              <ShareButtons
                url={`${window.location.origin}/prompts/${id}`}
                title={prompt.title}
                description={prompt.description || prompt.prompt_text?.substring(0, 160) || ''}
              />
            </div>
          </Card>

          {/* Prompt Text */}
          <Card padding="comfortable">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">Prompt</h2>
            <div
              className="prose prose-sm sm:prose max-w-none bg-gray-50 dark:bg-spooky-bg-light p-4 rounded-lg border dark:border-spooky-purple-mist dark:text-gray-300"
              dangerouslySetInnerHTML={{ __html: prompt.prompt_text }}
            />
          </Card>

          {/* Examples */}
          {prompt.examples && (
            <Card padding="comfortable">
              <h2 className="text-xl font-semibold mb-4 dark:text-white">Examples</h2>
              <div className="bg-gray-50 dark:bg-spooky-bg-light p-4 rounded-lg border dark:border-spooky-purple-mist whitespace-pre-wrap text-sm dark:text-gray-300">
                {prompt.examples}
              </div>
            </Card>
          )}

          {/* Ratings Section */}
          <RatingsSection
            ratings={ratings}
            myRating={myRating}
            ratingFormData={ratingFormData}
            setRatingFormData={setRatingFormData}
            onSubmit={handleRatingSubmit}
            onDelete={handleDeleteRating}
            currentUser={currentUser}
            isOwner={isOwner}
          />

          {/* Comments Section - Full Width in Mobile */}
          <div className="lg:hidden">
            <CommentSection promptId={id} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <PromptStats prompt={prompt} ratingStats={ratingStats} />
        </div>
      </div>

      {/* Comments Section - Desktop Only in Grid */}
      <div className="hidden lg:block mt-6">
        <CommentSection promptId={id} />
      </div>
    </div>
  );
};

export default PromptDetail;
